import {
  AdvancedAssignmentFilterOption,
  AssignmentDeviationEnum,
  AssignmentSortFieldEnum,
  AssignmentStatusEnum,
  CaseTypeEnum,
  DirectionEnum,
} from 'api/model';
import { useAssignmentSearchAssignmentWithPagingHook } from 'api/assignment/assignment';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';

const PAGE_SIZE = 50;

interface UseDayPlanningAssignmentsParams {
  date: Date;
  businessUnits: number[];
  caseTypes?: CaseTypeEnum[];
  assignmentStatuses?: AssignmentStatusEnum[];
  assignmentDeviations?: AssignmentDeviationEnum[];
  advancedFilters?: AdvancedAssignmentFilterOption[];
  searchText?: string;
  orderByField?: AssignmentSortFieldEnum;
  direction?: DirectionEnum;
}

const useDayPlanningAssignments = ({
  date,
  businessUnits,
  caseTypes,
  assignmentStatuses,
  assignmentDeviations,
  advancedFilters,
  searchText,
  orderByField,
  direction,
}: UseDayPlanningAssignmentsParams) => {
  const searchAssignmentWithPaging =
    useAssignmentSearchAssignmentWithPagingHook();
  const assignmentsResponse = useApiCallFn(
    (
      _date: Date,
      _businessUnits: number[],
      _caseTypes: CaseTypeEnum[] | undefined,
      _statuses: AssignmentStatusEnum[] | undefined,
      _deviations: AssignmentDeviationEnum[] | undefined,
      _advanced: AdvancedAssignmentFilterOption[] | undefined,
      _searchText: string | undefined,
      _orderByField: AssignmentSortFieldEnum | undefined,
      _direction: DirectionEnum | undefined,
      _skip: number
    ) =>
      searchAssignmentWithPaging({
          startDate: _date,
          endDate: _date,
          businessUnits:
            _businessUnits.length > 0 ? _businessUnits : undefined,
          caseTypes: _caseTypes?.length ? _caseTypes : undefined,
          assignmentStatuses: _statuses?.length ? _statuses : undefined,
          assignmentDeviations: _deviations?.length ? _deviations : undefined,
          advancedSearchOptions: _advanced?.length ? _advanced : undefined,
          searchStr: _searchText || undefined,
          orderByField: _orderByField,
          direction: _direction,
          forCurrentUser: false,
          size: PAGE_SIZE,
          skip: _skip,
        })
  );

  const update = useCallback(
    async (skip: number, clearFirst: boolean) => {
      if (clearFirst) {
        assignmentsResponse.setStatus(RequestStatus.Idle);
        assignmentsResponse.setResponse(undefined);
      }
      await assignmentsResponse.run(
        date,
        businessUnits,
        caseTypes,
        assignmentStatuses,
        assignmentDeviations,
        advancedFilters,
        searchText,
        orderByField,
        direction,
        skip
      );
    },
    [
      assignmentsResponse,
      date,
      businessUnits,
      caseTypes,
      assignmentStatuses,
      assignmentDeviations,
      advancedFilters,
      searchText,
      orderByField,
      direction,
    ]
  );

  // Re-fetch when filters or sort change (debounced)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      update(0, true);
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    date,
    businessUnits,
    caseTypes,
    assignmentStatuses,
    assignmentDeviations,
    advancedFilters,
    searchText,
    orderByField,
    direction,
  ]);

  const total = assignmentsResponse.response?.total ?? 0;
  const skip = assignmentsResponse.response?.skip ?? 0;
  const currentPage = Math.floor(skip / PAGE_SIZE);
  const totalPageCount = Math.ceil(total / PAGE_SIZE);

  const goToPage = useCallback(
    (page: number) => {
      update(page * PAGE_SIZE, false);
    },
    [update]
  );

  return useMemo(
    () => ({
      assignments: assignmentsResponse.response?.result ?? [],
      total,
      loading: assignmentsResponse.status === RequestStatus.Fetching,
      currentPage,
      totalPageCount,
      pageSize: PAGE_SIZE,
      goToPage,
      refetch: () => update(skip, false),
    }),
    [
      assignmentsResponse.response?.result,
      assignmentsResponse.status,
      total,
      currentPage,
      totalPageCount,
      goToPage,
      update,
      skip,
    ]
  );
};

export default useDayPlanningAssignments;
